"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createJuniAnalytics = void 0;
var ua_parser_js_1 = __importDefault(require("ua-parser-js"));
var anonymousId_1 = require("./anonymousId");
var durationTracker_1 = require("./durationTracker");
var VALID_ENVIRONMENTS = ['test', 'development', 'staging', 'production'];
// Dummy analytics that doesn't do anything but prevents calls from failing
var dummyAnalytics = {
    initialize: function () {
        console.error('Could not find Segment analytics. Analytics is disabled!');
    },
    identify: function () { return null; },
    page: function () { return null; },
    track: function () { return null; },
    trackWithDelay: function () { return Promise.resolve(); },
    addMiddleware: function () { return null; },
    removeMiddleware: function () { return null; },
    reset: function () { return null; },
};
function getPageProperties() {
    return {
        path: window.location.pathname,
        url: window.location.href,
        search: window.location.search,
        title: document.title,
    };
}
function getUserAgentProperties() {
    // extract out the key properties required
    var _a = (0, ua_parser_js_1.default)(), browser = _a.browser, os = _a.os, device = _a.device;
    return { browser: browser, os: os, device: device };
}
function createJuniAnalytics() {
    // check for SSR (e.g. Next.JS) or missing Segment
    if (typeof window === 'undefined' || !window.analytics) {
        return dummyAnalytics;
    }
    var initialized = false;
    var debug = false;
    var durationTracker = null;
    var currentUser = null;
    var userAgentProperties = getUserAgentProperties();
    // we need to manually attach traits because Segment doesn't do this on web (though it does on ios/android)
    // https://segment.com/docs/connections/spec/common/#context-fields-automatically-collected
    function getContext() {
        if (!currentUser) {
            return {};
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        var userId = currentUser.userId, traits = __rest(currentUser, ["userId"]);
        return { traits: traits };
    }
    var middlewares = [];
    // runs each middleware sequentially and exits early if any middleware returns null
    function processMiddleware(properties, type) {
        if (properties === void 0) { properties = {}; }
        var context = { path: window.location.pathname, type: type };
        return middlewares.reduce(function (prevProperties, middleware) {
            if (prevProperties === null)
                return null;
            if (typeof middleware === 'function')
                return middleware(prevProperties, context);
            return middleware.process(prevProperties, context);
        }, properties);
    }
    var previousPath;
    function checkInitialized() {
        var _a, _b;
        if (!initialized) {
            if ((_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.href) === null || _b === void 0 ? void 0 : _b.includes('://localhost')) {
                // throw error when local dev
                throw new Error('JuniAnalytics.initialize must be called before using JuniAnalytics');
            }
            console.error("JuniAnalytics.initialize must be called before using JuniAnalytics");
        }
    }
    function page() {
        checkInitialized();
        // allow page() to be called multiple times without consequence
        var newPath = window.location.pathname;
        if (previousPath === newPath) {
            return;
        }
        previousPath = newPath;
        // declare as a check against react router
        var properties = processMiddleware(getPageProperties(), 'page');
        // manually supply these fields because redirects may cause Segment to use a different location by the time it processes the event
        var context = __assign({ page: getPageProperties() }, getContext());
        if (properties === null) {
            return;
        }
        if (debug) {
            console.log('Juni Analytics: Page Event', properties);
        }
        window.analytics.page(properties, { context: context });
        durationTracker === null || durationTracker === void 0 ? void 0 : durationTracker.page();
    }
    function track(eventName, properties) {
        checkInitialized();
        var processedProperties = processMiddleware(__assign(__assign({}, properties), getPageProperties()), 'track');
        if (processedProperties === null) {
            return;
        }
        if (debug) {
            console.log('Juni Analytics: Track Event', processedProperties);
        }
        window.analytics.track(eventName, processedProperties, {
            context: getContext(),
        });
    }
    function trackWithDelay(eventName, properties) {
        track(eventName, properties);
        return new Promise(function (resolve) { return setTimeout(resolve, 300); });
    }
    function initialize(options) {
        if (initialized) {
            console.warn('Already initialized Juni Analytics');
            return;
        }
        var source = options.source, environment = options.environment, _a = options.middlewares, initialMiddlewares = _a === void 0 ? [] : _a;
        debug = !!options.debug;
        // set anonymous ID up
        setJuniAndSegmentAnonymousId();
        // set up middleware
        middlewares.push({
            name: 'default',
            process: function (properties) { return (__assign(__assign({ source: source, environment: normalizedEnvironment }, (options.disableUserAgentProperties ? {} : { userAgentProperties: userAgentProperties })), properties)); },
        });
        middlewares.push.apply(middlewares, initialMiddlewares);
        if (!source) {
            throw new Error("Source required for Juni Analytics");
        }
        // default to development if unknown environment
        var normalizedEnvironment = VALID_ENVIRONMENTS.includes(environment)
            ? environment
            : 'development';
        durationTracker = (0, durationTracker_1.trackPageDurations)(track);
        initialized = true;
    }
    var setJuniAndSegmentAnonymousId = function (forceNewToken) {
        if (forceNewToken === void 0) { forceNewToken = false; }
        var anonymousId = (0, anonymousId_1.getAndSetAnonymousId)(forceNewToken);
        window.analytics.setAnonymousId(anonymousId);
    };
    var removeMiddlewareByName = function (name) {
        if (!name)
            return false;
        var indexToRemove = middlewares.findIndex(function (middleware) { return middleware.name === name; });
        if (indexToRemove === -1)
            return false;
        while (indexToRemove !== -1) {
            middlewares.splice(indexToRemove, 1);
            indexToRemove = middlewares.findIndex(function (middleware) { return middleware.name === name; });
        }
        return true;
    };
    return {
        initialize: initialize,
        identify: function (user) {
            checkInitialized();
            if (JSON.stringify(user) === JSON.stringify(currentUser)) {
                return;
            }
            currentUser = user;
            if (debug) {
                console.log("Juni Analytics: Identify", user);
            }
            var userId = user.userId, traits = __rest(user, ["userId"]);
            window.analytics.identify(userId, traits);
        },
        addMiddleware: function (middleware) {
            var exists = middlewares.map(function (m) { return m.name; }).includes(middleware.name);
            if (exists) {
                console.log(middleware.name + " middleware already exists, removing existing instance(s) before adding new instance");
                removeMiddlewareByName(middleware.name);
            }
            middlewares.push(middleware);
            // reset segment anonymousId and juni_anonymous_id
            if (middleware.name === 'impersonation' && !exists) {
                window.analytics.reset();
                setJuniAndSegmentAnonymousId(true);
            }
            if (debug) {
                console.log(middlewares.map(function (m) { return m.name; }));
            }
        },
        removeMiddleware: function (name) {
            var wasFound = removeMiddlewareByName(name);
            // reset segment anonymousId and juni_anonymous_id
            if (name === 'impersonation' && wasFound) {
                window.analytics.reset();
                setJuniAndSegmentAnonymousId(true);
            }
            if (debug) {
                console.log(middlewares.map(function (m) { return m.name; }));
            }
        },
        page: page,
        track: track,
        trackWithDelay: trackWithDelay,
        reset: function () {
            window.analytics.reset();
            currentUser = null;
        },
    };
}
exports.createJuniAnalytics = createJuniAnalytics;
var JuniAnalytics = createJuniAnalytics();
exports.default = JuniAnalytics;
//# sourceMappingURL=JuniAnalytics.js.map